import { useEffect } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import SignUp from "../src/Pages/SignIn/SignUp";
import SignUpPhone from "../src/Pages/SignIn/SignUpPhone";
import SignUpPhoneOTP from "./Pages/SignIn/SignUpPhoneOTP";
import SignUpInfo from "../src/Pages/SignIn/SignUpInfo";
import SignInEmail from "./Pages/SignIn/SignInEmail";
import SignInPhone from "./Pages/SignIn/SignInPhone";
import SignInPhoneOTP from "./Pages/SignIn/SignInPhoneOTP";
import VerificationGoogle from "./Pages/SignIn/VerificationGoogle";
import GoogleAuthRedirect from "./Pages/SignIn/GoogleAuthRedirect";
import ForgotPW from "./Pages/SignIn/ForgotPW";
import Dashboard from "./Pages/Dashboard/Dashboard";
import Challenge from "./Pages/Challenge/Challenge";
import Overview from "./Pages/Challenge/Overview";
import TradeDetail from "./Pages/Challenge/TradeDetail";
import Withdraw from "./Pages/Transaction/Withdraw";
import History from "./Pages/Transaction/History";
import Information from "./Pages/Profile/Information";
import Security from "./Pages/Profile/Security";
import KYC from "./Pages/Profile/KYC";
import NotificationAll from "./Pages/Notification/NotificationAll";
import NotificationForYou from "./Pages/Notification/NotificationForYou";
import NotificationGeneral from "./Pages/Notification/NotificationGeneral";
import RuleGeneral from "./Pages/Rule/General";
import RuleChallenge from "./Pages/Rule/Challenge";
import RuleWithdraw from "./Pages/Rule/Withdraw";
import RuleInvite from "./Pages/Rule/Invite";
import InviteFriends from "./Pages/Invite/InviteFriends";
import WithdrawGift from "./Pages/Invite/WithdrawGift";
import DiscountForYou from "./Pages/Discount/ForYou";
import DiscountGeneral from "./Pages/Discount/General";
import CertificateGeneral from "./Pages/Certificate/General";
import CertificateForYou from "./Pages/Certificate/ForYou";
import TradeAccount from "./Pages/Trade/Account";
import TradeTrade from "./Pages/Trade/Test";
import AppShell from "./Components/AppShell";
import BuyStatus from "./Pages/Purchase/BuyStatus";
import ChallengePage from "./Pages/Purchase/ChallengePage";
import ChallengeFactorPage from "./Pages/Purchase/ChallengeFactorPage";
import PaymentMethodPage from "./Pages/Purchase/PaymentMethodPage";
import BuySuccessManual from "./Pages/Purchase/BuySuccessManual";
import DirectpayFinalized from "./Pages/Purchase/DirectpayFinalized";
import Support from "./Pages/Support/Support";
import { getRedirectFromNext } from "./helper/nextRedirect";
import Calculator from "./Pages/Calculator/Calculator";

const isAuthenticated = () => {
  const access = localStorage.getItem("access");
  const refresh = localStorage.getItem("refresh");
  return Boolean(access || refresh);
};

const RequireAuth = () => {
  const { search } = useLocation();
  const nextParam = new URLSearchParams(search).get("next");
  if (!isAuthenticated()) {
    return (
      <Navigate
        to={nextParam ? `/signin?next=${nextParam}` : "/signin"}
        replace
      />
    );
  }
  return <Outlet />;
};

const RootRedirect = () => {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const pathParam = searchParams.get("path");
  const nextParam = searchParams.get("next");
  const redirectPath = getRedirectFromNext(nextParam);
  const hasNextFlow = Boolean(redirectPath);

  useEffect(() => {
    if (!hasNextFlow) {
      return;
    }
    if (isAuthenticated()) {
      localStorage.removeItem("post_login_redirect");
      return;
    }
    localStorage.setItem("post_login_redirect", redirectPath);
  }, [hasNextFlow, redirectPath]);

  if (pathParam === "google") {
    return <GoogleAuthRedirect />;
  }

  if (hasNextFlow) {
    return isAuthenticated() ? (
      <Navigate to={redirectPath} replace />
    ) : (
      <Navigate to={`/signin?next=${nextParam}`} replace />
    );
  }

  return isAuthenticated() ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <Navigate to="/signin" replace />
  );
};

const NotFound = () => {
  useEffect(() => {
    window.location.replace("/error404.html");
  }, []);

  return null;
};

function App() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/signup" element={<SignUp />} />
      {/* <Route path="/signup/phone" element={<SignUpPhone />} />
      <Route path="/signup/phone/otp" element={<SignUpPhoneOTP />} />
      <Route path="/signup/info" element={<SignUpInfo />} /> */}
      <Route path="/signin" element={<Navigate to="/signin/email" replace />} />
      <Route path="/signin/email" element={<SignInEmail />} />
      <Route path="/signin/phone" element={<SignInPhone />} />
      <Route path="/signin/phone/otp" element={<SignInPhoneOTP />} />
      <Route path="/verification-google" element={<VerificationGoogle />} />
      <Route path="/forgot-password" element={<ForgotPW />} />
      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/challenge" element={<Challenge />} />
          <Route
            path="/challenge/account/:accountId/overview"
            element={<Overview />}
          />
          <Route
            path="/challenge/account/:accountId/trade-detail"
            element={<TradeDetail />}
          />
          <Route path="/Transaction/Withdraw" element={<Withdraw />} />
          <Route path="/Transaction/History" element={<History />} />
          <Route path="/profile/information" element={<Information />} />
          <Route path="/profile/security" element={<Security />} />
          <Route path="/profile/kyc" element={<KYC />} />
          <Route path="/notification/all" element={<NotificationAll />} />
          {/* <Route
            path="/notification/for-you"
            element={<NotificationForYou />}
          />
          <Route
            path="/notification/general"
            element={<NotificationGeneral />}
          /> */}
          <Route path="/support" element={<Support />} />
          <Route path="/rule/general" element={<RuleGeneral />} />
          <Route path="/rule/challenge" element={<RuleChallenge />} />
          <Route path="/rule/withdraw" element={<RuleWithdraw />} />
          <Route path="/rule/invite" element={<RuleInvite />} />
          <Route path="/invite" element={<InviteFriends />} />
          <Route path="/invite/gift" element={<WithdrawGift />} />
          {/* <Route path="/discount/for-you" element={<DiscountForYou />} /> */}
          <Route path="/discount/general" element={<DiscountGeneral />} />
          <Route path="/discount" element={<DiscountForYou />} />
          <Route path="/certificate/general" element={<CertificateGeneral />} />
          <Route path="/certificate/for-you" element={<CertificateForYou />} />
          <Route path="/trade/account" element={<TradeAccount />} />
          <Route path="/trade/:accountId/trade" element={<TradeTrade />} />
          <Route path="/purchase" element={<ChallengePage />} />
          <Route path="/purchase/confirm" element={<ChallengeFactorPage />} />
          <Route
            path="/purchase/confirm/payment"
            element={<PaymentMethodPage />}
          />
          <Route path="/purchase/finalized" element={<BuyStatus />} />
          <Route
            path="/purchase/finalized/directpay"
            element={<DirectpayFinalized />}
          />
          <Route
            path="/purchase/finalized/manual"
            element={<BuySuccessManual />}
          />
          <Route path="/calculator" element={<Calculator />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
